import {
  Avatar,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  Option,
  Select,
  Switch,
  Textarea,
  Typography
} from "@material-tailwind/react";
import {XMarkIcon} from "@heroicons/react/24/outline";
import Dict from "../../../helper/dict";
import React, {useEffect, useState} from "react";
import SelectInput from "../../system/select";
import "../../../style/datePicker.css"
import {getProvinceCities} from "../../../services/city/city";
import {getProvinces} from "../../../services/province/province";
import Validator from "../../../helper/validator/Validator";
import DateField from "../../system/dateField";
import {submitBackOfficePerson, updateBackOfficePerson} from "../../../services/back_office/person";
import FileInput from "../../system/fileInput";
import {ValidatorProvider} from "../../../helper/validator/ValidatorContext";
import {checkAll} from "../../../helper/validator/Check";
import {toast} from "react-toastify";
import {getFileUrl} from "../../../config";

const FormSection = ({title, children, className = ""}) => (
  <section className={`rounded-lg border border-blue-gray-50 bg-white p-4 ${className}`}>
    <Typography variant="small" color="blue-gray" className="mb-5 font-bold">
      {title}
    </Typography>
    {children}
  </section>
);

const FieldWrapper = ({children}) => (
  <div className="flex min-h-[76px] w-full flex-col">
    {children}
  </div>
);

export function BackOfficePersonForm({person, open, setOpen, setLastDataRefresh, setGridSelectedRow}) {
  const handleOpen = () => setOpen(!open);

  const avatar = '/assets/blank_profile.jpg';

  const [id, setId] = useState(person?.id ?? null)
  const [name, setName] = useState(person?.name ?? null)
  const [family, setFamily] = useState(person?.family ?? null)
  const [phone, setPhone] = useState(person?.phone ?? null)
  const [photo, setPhoto] = useState(person?.photo ?? null)
  const [status, setStatus] = useState(person?.status ?? true)
  const [personDataId, setPersonDataId] = useState(person?.person_data_id ?? null)
  const [fatherName, setFatherName] = useState(person?.father_name ?? null)
  const [nationalCode, setNationalCode] = useState(person?.national_code ?? null)
  const [birthDate, setBirthDate] = useState(person?.birth_date ?? null)
  const [gender, setGender] = useState(person?.gender ?? null)
  const [provinceId, setProvinceId] = useState(person?.province_id ?? null)
  const [cityId, setCityId] = useState(person?.city_id ?? null)
  const [homePhone, setHomePhone] = useState(person?.home_phone ?? null)
  const [email, setEmail] = useState(person?.email ?? null)
  const [address, setAddress] = useState(person?.address ?? null)

  const [password, setPassword] = useState("");

  const [isChecked, setIsChecked] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const isEditMode = Boolean(id);
  const fullName = `${name || ""} ${family || ""}`.trim();
  const profilePhoto = typeof photo === "string" && photo.length > 0 ? getFileUrl(photo) : avatar;

  useEffect(() => {
    setId(person?.id ?? null)
    setName(person?.name ?? null)
    setFamily(person?.family ?? null)
    setPhone(person?.phone ?? null)
    setPhoto(person?.photo ?? null)
    setStatus(person?.status !== "disable")
    setPersonDataId(person?.person_data_id ?? null)
    setFatherName(person?.father_name ?? null)
    setNationalCode(person?.national_code ?? null)
    setBirthDate(person?.birth_date ?? null)
    setGender(person?.gender ?? null)
    setProvinceId(person?.province_id ?? null)
    setCityId(person?.city_id ?? null)
    setHomePhone(person?.home_phone ?? null)
    setEmail(person?.email ?? null)
    setAddress(person?.address ?? null)
    setPassword("")
    setIsChecked(false)
  }, [person]);

  const save = async () => {
    if (isSaving) {
      return
    }

    setIsChecked(true)
    const res = checkAll();
    if (!res) {
      toast.error("مقادیر الزامی را بر اساس راهنما تکمیل کنید")
      return
    }

    setIsSaving(true)

    const data = {
      id: id,
      name: name,
      family: family,
      phone: phone,
      photo: photo,
      status: status === true ? "enable" : "disable",
      father_name: fatherName,
      national_code: nationalCode,
      birth_date: birthDate,
      gender: gender,
      province_id: provinceId,
      city_id: cityId,
      home_phone: homePhone,
      email: email,
      address: address,
      password: password,
      person_data_id: personDataId ? parseInt(personDataId) : null,
    };
    let result;

    try {
      if (!id) {
        result = await submitBackOfficePerson(data);
      } else {
        result = await updateBackOfficePerson(data);
      }

      if (result.status === 200) {
        setGridSelectedRow(false)
        setLastDataRefresh(Date.now())
        handleOpen()
      }
    } finally {
      setIsSaving(false)
    }
  };

  return (
    <>
      <ValidatorProvider>
        <Dialog
          open={open}
          handler={handleOpen}
          size="xl"
          className="max-h-[92vh] overflow-hidden"
          animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}
        >
          <DialogHeader className="flex items-start justify-between border-b border-blue-gray-50 px-5 py-4">
            <div className="flex items-center gap-4">
              <Avatar
                variant="circular"
                size="md"
                src={profilePhoto}
                alt="پروفایل"
                className="border border-blue-gray-50"
              />
              <div>
                <Typography variant="h6" color="blue-gray">
                  {isEditMode ? "ویرایش کاربر" : "ایجاد کاربر جدید"}
                </Typography>
                <Typography variant="small" color="gray" className="mt-1 font-normal">
                  {fullName || phone || "کاربر جدید"}
                </Typography>
              </div>
            </div>
            <Button variant="text" color="gray" className="h-9 w-9 p-0" onClick={handleOpen}>
              <XMarkIcon className="h-5 w-5"/>
            </Button>
          </DialogHeader>
          <DialogBody className="max-h-[72vh] overflow-y-auto bg-blue-gray-50/30 px-5 py-5">
            <div className="grid grid-cols-1 gap-4">
              <FormSection title="اطلاعات اصلی">
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
                  <FieldWrapper>
                    <Input variant="standard"
                           value={name}
                           label={Dict('name')}
                           placeholder={Dict('name')}
                           onChange={(e) => {
                             setName(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={name} rules="required|alphabetic"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Input variant="standard"
                           value={family}
                           label={Dict('family')}
                           placeholder={Dict('family')}
                           onChange={(e) => {
                             setFamily(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={family} rules="required|alphabetic"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Input variant="standard"
                           value={phone}
                           label={Dict('phone')}
                           placeholder={Dict('phone')}
                           onChange={(e) => {
                             setPhone(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={phone} rules="required|mobile"/>
                  </FieldWrapper>

                  {!id &&
                    <FieldWrapper>
                      <Input variant="standard"
                             value={password}
                             label={Dict('password')}
                             placeholder={Dict('password')}
                             onChange={(e) => {
                               setPassword(e.target.value)
                             }}
                      />
                      <Validator isChecked={isChecked} state={password} rules="password"/>
                    </FieldWrapper>
                  }
                </div>
              </FormSection>

              <FormSection title="اطلاعات هویتی">
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
                  <FieldWrapper>
                    <Input variant="standard"
                           value={nationalCode}
                           label={Dict('national_code')}
                           placeholder={Dict('national_code')}
                           onChange={(e) => {
                             setNationalCode(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={nationalCode} rules="num|len=10"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Input variant="standard"
                           value={fatherName}
                           label={Dict('father_name')}
                           placeholder={Dict('father_name')}
                           onChange={(e) => {
                             setFatherName(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={fatherName} rules="alphabetic"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <DateField
                      className="my-4 mx-4 w-full"
                      date={birthDate}
                      setDate={setBirthDate}
                      label={Dict("birth_date")}
                    />
                    <Validator isChecked={isChecked} state={birthDate} rules=""/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Select variant="static" className="rtl" style={{label: {left: ""}}}
                            label={Dict('gender')}
                            value={gender}
                            onChange={(val) => setGender(val)}>
                      <Option value="male">مرد</Option>
                      <Option value="female">زن</Option>
                    </Select>
                    <Validator isChecked={isChecked} state={gender} rules=""/>
                  </FieldWrapper>
                </div>
              </FormSection>

              <FormSection title="اطلاعات تماس و محل سکونت">
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
                  <FieldWrapper>
                    <Input variant="standard"
                           value={homePhone}
                           label={Dict('home_phone')}
                           placeholder={Dict('home_phone')}
                           onChange={(e) => {
                             setHomePhone(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={homePhone} rules="phoneNumber"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Input variant="standard"
                           value={email}
                           label={Dict('email')}
                           placeholder={Dict('email')}
                           onChange={(e) => {
                             setEmail(e.target.value)
                           }}
                    />
                    <Validator isChecked={isChecked} state={email} rules="email"/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <SelectInput
                      className="w-full"
                      label={Dict("province")}
                      defaultValue={provinceId}
                      autoLoadService={getProvinces}
                      onChange={setProvinceId}
                    />
                    <Validator isChecked={isChecked} state={provinceId} rules=""/>
                  </FieldWrapper>

                  <FieldWrapper>
                    <SelectInput
                      className="w-full"
                      label={Dict("city")}
                      defaultValue={cityId}
                      refreshSignal={provinceId}
                      autoLoadService={getProvinceCities}
                      autoLoadServiceData={provinceId}
                      onChange={setCityId}
                    />
                    <Validator isChecked={isChecked} state={cityId} rules=""/>
                  </FieldWrapper>
                </div>

                <div className="mt-2">
                  <Textarea
                    variant="standard"
                    value={address}
                    label={Dict('address')}
                    onChange={(e) => {
                      setAddress(e.target.value)
                    }}
                  />
                  <Validator isChecked={isChecked} state={address} rules=""/>
                </div>
              </FormSection>

              <FormSection title="وضعیت و فایل‌ها">
                <div className="grid grid-cols-1 items-center gap-6 xl:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Switch
                      label={
                        <Typography color="blue-gray" className="font-medium">
                          {status ? "کاربر فعال است" : "کاربر غیرفعال است"}
                        </Typography>
                      }
                      checked={status}
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.checked)
                      }}
                      labelProps={{className: "mx-4"}}
                    />
                  </div>

                  <FieldWrapper>
                    <FileInput
                      id="back-office-person-photo"
                      classes="w-full justify-start"
                      label={Dict('profile_photo')}
                      fileCode={photo}
                      onChange={(e) => {
                        setPhoto(e.target.files[0]);
                      }}/>
                    <Validator isChecked={isChecked} state={photo} rules=""/>
                  </FieldWrapper>
                </div>
              </FormSection>
            </div>
          </DialogBody>
          <DialogFooter className="border-t border-blue-gray-50 px-5 py-4">
            <Button variant="text" color="gray" onClick={handleOpen}>
              <span>انصراف</span>
            </Button>
            <Button
              variant="gradient"
              color="gray"
              onClick={save}
              disabled={isSaving}
              className="ml-1 min-w-24"
            >
              <span>{isSaving ? "در حال ذخیره..." : "ذخیره"}</span>
            </Button>
          </DialogFooter>
        </Dialog>
      </ ValidatorProvider>
    </>
  );
}
